/** Unwrap API ids that may be `{ value: n }`. */
function unwrapId (v) {
  if (v == null || v === '') return null
  if (typeof v === 'object' && v.value !== undefined) return unwrapId(v.value)
  return v
}

/** Raw array from `GET /api/v1/sellers` body (local and prod shapes). */
export function extractV1SellersItems (body) {
  if (!body) return []
  if (Array.isArray(body)) return body
  var keys = ['item', 'items', 'content', 'sellers', 'sellerAccounts', 'seller_accounts', 'data']
  for (var i = 0; i < keys.length; i++) {
    var arr = body[keys[i]]
    if (Array.isArray(arr)) return arr
  }
  return []
}

/**
 * Map v1 seller rows → `{ id, name, region?, marketplace? }` for filter UIs.
 * Matches connected storefronts, not catalog-derived `availableFilters.sellerAccounts`.
 */
export function mapV1SellersToFilterAccounts (items) {
  if (!Array.isArray(items)) return []
  return items.map(function (u) {
    if (!u) return null
    var sid = unwrapId(u.sellerId != null ? u.sellerId : u.seller_id)
    if (sid == null || sid === '') sid = unwrapId(u.id)
    if (sid == null || sid === '') return null
    var name = u.nickName || u.nickname || u.name || u.accountNickName || u.storefrontName
    if (!name) name = 'Storefront ' + String(sid).slice(-6)
    var n = Number(sid)
    var id = isNaN(n) ? sid : n
    var region = u.region || u.marketplaceRegion || 'NA'
    var mp = u.marketplace || u.marketplaceIso31662Code || u.marketplaceCode
    return { id: id, name: name, region: region, marketplace: mp }
  }).filter(Boolean)
}

/** Normalize SP country/marketplace token for filter dropdowns (UK alias). */
function normalizeMarketplaceFilterCode (raw) {
  if (raw == null || raw === '') return null
  var m = String(raw).trim().toUpperCase()
  if (!m) return null
  if (m === 'GB') m = 'UK'
  return m
}

/**
 * Collect marketplace codes from GET /api/v1/sellers for Issues / filter UIs.
 * Reads root `availableFilters.marketplaces`, top-level marketplace arrays, and per-item fields.
 *
 * @param {object} body - Parsed JSON body
 * @returns {string[]} Unique uppercase codes (e.g. US, UK)
 */
export function extractMarketplaceCodesFromV1SellersBody (body) {
  var seen = {}
  var list = []
  function pushRaw (raw) {
    var c = normalizeMarketplaceFilterCode(raw)
    if (!c || seen[c]) return
    seen[c] = true
    list.push(c)
  }
  function pushEntry (m) {
    if (m == null) return
    if (typeof m === 'string') {
      pushRaw(m)
      return
    }
    if (typeof m === 'object') {
      pushRaw(m.code || m.marketplace || m.marketplaceCode || m.marketplace_code || m.countryCode || m.country_code)
    }
  }

  if (!body || typeof body !== 'object') return list

  var af = body.availableFilters || body.available_filters
  if (af && Array.isArray(af.marketplaces)) {
    for (var ai = 0; ai < af.marketplaces.length; ai++) pushEntry(af.marketplaces[ai])
  }

  var rootArrays = ['availableMarketplaces', 'available_marketplaces', 'marketplaces']
  for (var ri = 0; ri < rootArrays.length; ri++) {
    var arr = body[rootArrays[ri]]
    if (!Array.isArray(arr)) continue
    for (var aj = 0; aj < arr.length; aj++) pushEntry(arr[aj])
  }

  var items = extractV1SellersItems(body)
  for (var ii = 0; ii < items.length; ii++) {
    var u = items[ii]
    if (!u || typeof u !== 'object') continue
    pushRaw(u.marketplace || u.marketplaceIso31662Code || u.marketplaceCode || u.marketplace_code)

    var nestedKeys = ['marketplaces', 'listed_marketplaces', 'marketplaceCodes', 'marketplace_codes', 'sellerMarketplaces', 'seller_marketplaces', 'marketplaceParticipations']
    for (var nk = 0; nk < nestedKeys.length; nk++) {
      var nested = u[nestedKeys[nk]]
      if (!Array.isArray(nested)) continue
      for (var ni = 0; ni < nested.length; ni++) {
        var el = nested[ni]
        if (typeof el === 'string') pushRaw(el)
        else if (el && typeof el === 'object') {
          pushRaw(el.marketplace || el.marketplaceCode || el.marketplace_code || el.code || el.countryCode)
        }
      }
    }
  }

  return list
}

/** Display order for navbar marketplace filters (divider `s` handled in UI). */
export var DEFAULT_MARKETPLACE_FILTER_ORDER = [
  'US', 'CA', 'MX', 'BR',
  'UK', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'SE', 'PL', 'AT',
  'IN', 'JP', 'AU', 'SG',
  'AE', 'SA', 'EG', 'TR', 'ZA'
]

/**
 * Preferred marketplace list for filter dropdowns (`s` = NA/EU divider in Semantic UI menus).
 * Unknown codes from the API are still shown — see `buildMarketplaceFilterDropdownItems`.
 */
export var MARKETPLACE_FILTER_COUNTRIES_ALL = [
  'US', 'CA', 'MX', 'BR', 's',
  'UK', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'SE', 'PL', 'AT', 'IN',
  'JP', 'AU', 'SG',
  'AE', 'SA', 'EG', 'TR', 'ZA'
]

/** Amazon TLD suffix for navbar labels (e.g. Amazon.com.au). */
export var MARKETPLACE_AMAZON_SUFFIX = {
  US: 'com',
  CA: 'ca',
  MX: 'com.mx',
  BR: 'com.br',
  UK: 'co.uk',
  DE: 'de',
  FR: 'fr',
  ES: 'es',
  IT: 'it',
  NL: 'nl',
  BE: 'be',
  SE: 'se',
  PL: 'pl',
  AT: 'at',
  IN: 'in',
  JP: 'co.jp',
  AU: 'com.au',
  SG: 'sg',
  AE: 'ae',
  SA: 'sa',
  EG: 'eg',
  TR: 'com.tr',
  ZA: 'co.za'
}

export function getMarketplaceAmazonSuffix (country) {
  if (!country) return ''
  var code = String(country).trim().toUpperCase()
  if (code === 'GB') code = 'UK'
  return MARKETPLACE_AMAZON_SUFFIX[code] || code.toLowerCase()
}

/**
 * Union marketplace codes for filter dropdowns: product catalog filters + connected seller accounts.
 *
 * @param {object} [opts]
 * @param {Array<string|{code?: string}>} [opts.fromAvailableFilters] - `availableFilters.marketplaces`
 * @param {object} [opts.v1SellersBody] - parsed GET /api/v1/sellers body
 * @param {string[]} [opts.canonicalOrder] - preferred sort order; other codes append at end
 * @returns {string[]}
 */
export function buildMarketplaceFilterCodes (opts) {
  opts = opts || {}
  var canonicalOrder = opts.canonicalOrder || DEFAULT_MARKETPLACE_FILTER_ORDER
  var seen = {}
  var merged = []

  function add (raw) {
    var c = normalizeMarketplaceFilterCode(raw)
    if (!c || seen[c]) return
    seen[c] = true
    merged.push(c)
  }

  var af = opts.fromAvailableFilters
  if (Array.isArray(af)) {
    for (var i = 0; i < af.length; i++) {
      var m = af[i]
      add(typeof m === 'string' ? m : (m && (m.code || m.marketplace || m.marketplaceCode)))
    }
  }

  if (opts.v1SellersBody) {
    var fromV1 = extractMarketplaceCodesFromV1SellersBody(opts.v1SellersBody)
    for (var v = 0; v < fromV1.length; v++) add(fromV1[v])
  }

  var sorted = []
  var canonicalSet = {}
  for (var k = 0; k < canonicalOrder.length; k++) {
    var key = canonicalOrder[k]
    if (key === 's') continue
    canonicalSet[key] = true
    if (seen[key]) sorted.push(key)
  }
  for (var j = 0; j < merged.length; j++) {
    if (!canonicalSet[merged[j]] && sorted.indexOf(merged[j]) < 0) sorted.push(merged[j])
  }
  return sorted
}

/**
 * Build marketplace dropdown rows from account-available codes.
 * Keeps canonical order (and `s` divider) from `countriesAll`, then appends any
 * extra codes present in Product Tracking / API that are not yet in the whitelist.
 *
 * @param {string[]} userCountries - uppercase codes available for this account
 * @param {string[]} [countriesAll] - ordered list possibly including divider `s`
 * @returns {string[]}
 */
export function buildMarketplaceFilterDropdownItems (userCountries, countriesAll) {
  var allowed = Array.isArray(userCountries) ? userCountries : []
  var all = Array.isArray(countriesAll) && countriesAll.length
    ? countriesAll
    : MARKETPLACE_FILTER_COUNTRIES_ALL

  var allowedSet = {}
  for (var i = 0; i < allowed.length; i++) {
    var code = normalizeMarketplaceFilterCode(allowed[i])
    if (code) allowedSet[code] = true
  }

  var out = []
  var seen = {}
  for (var j = 0; j < all.length; j++) {
    var c = all[j]
    if (c === 's') {
      out.push('s')
      continue
    }
    var n = normalizeMarketplaceFilterCode(c)
    if (!n || !allowedSet[n] || seen[n]) continue
    seen[n] = true
    out.push(n)
  }

  for (var k = 0; k < allowed.length; k++) {
    var extra = normalizeMarketplaceFilterCode(allowed[k])
    if (!extra || seen[extra]) continue
    seen[extra] = true
    out.push(extra)
  }

  while (out.length && out[0] === 's') out.shift()
  while (out.length && out[out.length - 1] === 's') out.pop()
  var cleaned = []
  for (var m = 0; m < out.length; m++) {
    if (out[m] === 's' && (!cleaned.length || cleaned[cleaned.length - 1] === 's')) continue
    cleaned.push(out[m])
  }
  return cleaned
}



// WEBPACK FOOTER //
// ./src/utils/sellerAccountsFromV1Api.js